import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Check, X as XIcon } from "lucide-react";
import { evaluatePasswordStrength } from "@/lib/passwordStrength";
import { RecaptchaCheckbox } from "@/components/RecaptchaCheckbox";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [saving, setSaving] = useState(false);

  const strength = evaluatePasswordStrength(password);
  const mismatch = confirm && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing reset token — please use the link from your email.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    if (!captchaToken) return toast.error("Please complete the 'I'm not a robot' check.");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password, recaptcha_token: captchaToken });
      toast.success("Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, "Could not reset password"));
    } finally {
      setSaving(false);
    }
  };

  const submitDisabled = saving || !token || password.length < 6 || !confirm || password !== confirm || !captchaToken;

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="reset-password-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-sage-dark" />
          <p className="text-xs font-bold uppercase tracking-wide text-forest/60">Reset password</p>
        </div>
        <h1 className="text-2xl font-black text-forest mb-6">Choose a new password</h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-forest/80 mb-2 block">New password</Label>
            <Input type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl bg-softstone/60 border-sage/30" data-testid="reset-password-input" required />
            {password && (
              <div className="mt-1.5" data-testid="reset-password-strength">
                <div className="h-1.5 w-full bg-sage-light/40 rounded-full overflow-hidden">
                  <div className={`h-full ${strength.color} transition-all`} style={{ width: `${strength.score}%` }} data-testid="reset-password-strength-bar" />
                </div>
                <p className="text-[11px] text-forest/60 mt-1" data-testid="reset-password-strength-label">Strength: <span className="font-bold">{strength.label}</span></p>
              </div>
            )}
          </div>
          <div>
            <Label className="text-forest/80 mb-2 block">Confirm new password</Label>
            <Input type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="rounded-xl bg-softstone/60 border-sage/30" data-testid="reset-password-confirm" required />
            {confirm && (
              mismatch ? (
                <p className="text-[11px] text-terracotta-dark mt-1 flex items-center gap-1" data-testid="reset-password-mismatch">
                  <XIcon className="w-3 h-3" /> Passwords do not match
                </p>
              ) : (
                <p className="text-[11px] text-sage-dark mt-1 flex items-center gap-1" data-testid="reset-password-match">
                  <Check className="w-3 h-3" /> Passwords match
                </p>
              )
            )}
          </div>
          <RecaptchaCheckbox onChange={setCaptchaToken} testId="reset-password-recaptcha" />
          <Button type="submit" disabled={submitDisabled}
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md px-8"
            data-testid="reset-password-submit">
            {saving ? "Resetting..." : "Reset password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
